import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import { MapPin, ChevronRight, Building2 } from 'lucide-react';
import { locationsService } from '../../services/locationsService';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { getRestaurantFromStorage } from '../../services/restaurant';

/**
 * Purchases are received per location/branch — each holds its own stock. This
 * landing page lets the user pick which location to buy for; a single-location
 * restaurant skips straight in.
 */
export default function PurchaseLocationPicker({ restaurantId: propRestaurantId }) {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId } = useParams();
  const { restaurantId: contextRestaurantId } = useRestaurant();
  const { user } = useAuth();

  const restaurantId =
    propRestaurantId ||
    urlRestaurantId ||
    contextRestaurantId ||
    user?.restaurant_id ||
    user?.restaurant ||
    getRestaurantFromStorage()?.id;

  const { toast } = useToast();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const goToLocation = useCallback(
    (locationId, replace = false) =>
      navigate(`/restaurant/${restaurantId}/locations/${locationId}/purchase`, { replace }),
    [navigate, restaurantId],
  );

  const loadLocations = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await locationsService.list(restaurantId);
      const active = (Array.isArray(rows) ? rows : []).filter((l) => l.is_active !== false);
      setLocations(active);
      if (active.length === 1) {
        goToLocation(active[0].id, true);
      }
    } catch (error) {
      console.error('PurchaseLocationPicker: failed to load locations', error);
      toast({
        title: 'Error',
        description: error?.error || error?.message || 'Failed to load locations',
        variant: 'destructive',
      });
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, goToLocation, toast]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  if (!restaurantId) {
    return (
      <div className="p-6 w-full">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 text-lg mb-4">
              No restaurant selected. Please select a restaurant first.
            </p>
            <Button onClick={() => navigate('/restaurant-setup')}>Go to Restaurant Setup</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      <div className="mb-8">
        <h1 className="font-display text-4xl font-semibold text-foreground tracking-tight leading-none">Purchase</h1>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading locations…</div>
      ) : locations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-display text-xl text-foreground mb-2">No locations yet</h3>
            <p className="text-muted-foreground mb-4">
              Purchasing belongs to a location. Create a location first, then record its purchases.
            </p>
            <Button onClick={() => navigate(`/restaurant/${restaurantId}/locations`)}>
              Manage Locations
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {locations.map((loc) => (
            <Card
              key={loc.id}
              role="button"
              tabIndex={0}
              onClick={() => goToLocation(loc.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToLocation(loc.id);
                }
              }}
              className="cursor-pointer hover:shadow-md hover:border-primary/40 transition-all"
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-accent-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg font-semibold text-foreground tracking-tight truncate">
                      {loc.name}
                    </h3>
                    {loc.is_primary && (
                      <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-success-soft text-success">
                        Primary
                      </span>
                    )}
                  </div>
                  {[loc.address_line1, loc.city, loc.state].filter(Boolean).length > 0 && (
                    <p className="text-sm text-muted-foreground truncate">
                      {[loc.address_line1, loc.city, loc.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
